/** Locale-aware formatting of dates, counts and sizes, identical on server and browser. */
import { LANGUAGE_TAGS, type Locale } from "#/lib/locale.ts";

/**
 * Formats an ISO timestamp as a date and time in UTC for the given locale.
 *
 * The time zone is fixed so the server and the browser produce the same text
 * and hydration never replaces it.
 *
 * @param iso - ISO 8601 timestamp.
 * @param locale - Locale whose conventions to use.
 * @returns For example `27 Sept 2026, 09:27 UTC`.
 */
export function formatTimestamp(iso: string, locale: Locale): string {
  const text = new Intl.DateTimeFormat(LANGUAGE_TAGS[locale], {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  }).format(new Date(iso));
  return `${text} UTC`;
}

/**
 * Formats a whole number with the locale's digit grouping.
 *
 * @param value - The number.
 * @param locale - Locale whose conventions to use.
 * @returns For example `1,463` in English or `1.463` in German.
 */
export function formatCount(value: number, locale: Locale): string {
  return new Intl.NumberFormat(LANGUAGE_TAGS[locale]).format(value);
}

/**
 * Formats a byte size in kilobytes (1 KB = 1,000 bytes) with one decimal.
 *
 * @param bytes - Size in bytes.
 * @param locale - Locale whose conventions to use.
 * @returns For example `5.9 kB`.
 */
export function formatBytes(bytes: number, locale: Locale): string {
  return new Intl.NumberFormat(LANGUAGE_TAGS[locale], {
    style: "unit",
    unit: "kilobyte",
    unitDisplay: "short",
    maximumFractionDigits: 1,
  }).format(bytes / 1000);
}

/**
 * Formats an ISO calendar date (no time) for the given locale, in UTC so it
 * never shifts by a day between server and browser.
 *
 * @param isoDate - Date such as `2026-09-27`.
 * @param locale - Locale whose conventions to use.
 * @returns For example `27 September 2026` or `27. September 2026`.
 */
export function formatDate(isoDate: string, locale: Locale): string {
  return new Intl.DateTimeFormat(LANGUAGE_TAGS[locale], { dateStyle: "long", timeZone: "UTC" }).format(new Date(`${isoDate}T00:00:00Z`));
}

/**
 * Splits a command or code span into tokens and the whitespace between them.
 * Browsers break lines after a hyphen, so `--project` could wrap as `-` and
 * `-project` and a command copied by eye would be wrong; the stylesheet makes
 * each token one unit that moves to the next line whole, so a command wraps
 * between tokens, and inside a token only when it is wider than the line.
 *
 * @param text - The command or code text.
 * @returns Its parts in order; `whole` is true for a token, false for whitespace.
 */
export function codeTokens(text: string): { text: string; whole: boolean }[] {
  return text
    .split(/(\s+)/)
    .filter((part) => part !== "")
    .map((part) => ({ text: part, whole: !/^\s/.test(part) }));
}
